import React, { useState, useEffect, useCallback, useRef } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Download as DownloadIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  ArrowForward as ForwardIcon,
  ArrowBack as BackwardIcon,
  DeleteForever as DeleteIcon,
  Nfc as NfcIcon,
  Sensors as ScanGunIcon,
  ConfirmationNumber as PermitNumberIcon,
  AccountBalance as AuthorityIcon,
  Category as CategoryIcon,
  Info as InfoIcon,
  Event as EventIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  Update as UpdateIcon,
  DirectionsCar as VehicleIcon,
  Factory as MakeIcon,
  CalendarToday as CalendarIcon,
  EventSeat as SeatIcon,
  Person as PersonIcon,
  Badge as CnicIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as AddressIcon,
  Description as DescriptionIcon,
  Notes as NotesIcon,
  AltRoute as RouteIcon,
  Block as RestrictionsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiClient, { BACKEND_BASE_URL } from '../services/apiClient';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { hasFeature } from '../utils/permissions';
import { formatDate } from '../utils/formatDate';
import PageLoader from '../components/PageLoader';
import PermissionTooltip from '../components/PermissionTooltip';

const STATUS_DISPLAY_LABELS = {
  inactive: 'Suspended',
};

const getStatusLabel = (status) => STATUS_DISPLAY_LABELS[status] || status;

const TERMINAL_STATUS_COLORS = {
  active: '#2e7d32',
  inactive: '#757575',
  cancelled: '#c62828',
  expired: '#e65100',
};

const WorkflowProgressBar = ({ progress, permitStatus, isEndUser = false }) => {
  const { t } = useTranslation();
  const { steps, current_step_index, percentage, is_terminal, terminal_status, workflow_name } = progress;

  const barColor = is_terminal
    ? (TERMINAL_STATUS_COLORS[terminal_status] || '#9e9e9e')
    : '#1565c0';

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
          {t('permitDetails.approvalProgress')}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          {workflow_name} &mdash; {percentage}%
        </Typography>
      </Box>

      {/* Linear bar */}
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          mb: 2,
          bgcolor: '#e3e8ef',
          '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 4 },
        }}
      />

      {/* Step indicators */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
        {steps.map((step, idx) => {
          const isDone = idx < current_step_index || (is_terminal && idx <= current_step_index);
          const isCurrent = !is_terminal && idx === current_step_index;
          const isCompleted = is_terminal || isDone;

          return (
            <Box key={step.role} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
              {/* Connector line between steps */}
              {idx > 0 && (
                <Box sx={{
                  position: 'absolute',
                  top: 10,
                  left: '-50%',
                  width: '100%',
                  height: 2,
                  bgcolor: isDone || is_terminal ? barColor : '#ddd',
                  zIndex: 0,
                }} />
              )}
              <Tooltip title={isEndUser ? t('permitDetails.stepNumber', { number: idx + 1 }) : step.label} arrow>
                <Box sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: isCurrent ? barColor : isCompleted ? barColor : '#ddd',
                  border: isCurrent ? `3px solid ${barColor}` : '2px solid transparent',
                  boxShadow: isCurrent ? `0 0 0 3px ${barColor}33` : 'none',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'default',
                  transition: 'all 0.2s',
                }}>
                  {isCompleted && (
                    <Box component="span" sx={{ color: '#fff', fontSize: 12, lineHeight: 1 }}>✓</Box>
                  )}
                  {isCurrent && (
                    <Box component="span" sx={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>●</Box>
                  )}
                </Box>
              </Tooltip>
              <Typography variant="caption" sx={{
                mt: 0.5,
                fontSize: '0.62rem',
                color: isCurrent ? barColor : isCompleted ? '#555' : '#aaa',
                fontWeight: isCurrent ? 700 : 400,
                textAlign: 'center',
                maxWidth: 60,
                wordBreak: 'break-word',
                lineHeight: 1.2,
              }}>
                {isEndUser ? t('permitDetails.stepNumber', { number: idx + 1 }) : step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

// Uniform "icon + label + value" tile used across the Basic Info / Vehicle /
// Owner / Additional Details tabs, so every field reads the same way.
const DetailCard = ({ icon: Icon, label, value, accentColor, fullWidth = false, sm, pill = false }) => (
  <Grid item xs={12} sm={sm || (fullWidth ? 12 : 6)}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 2,
        height: '100%',
        borderRadius: 2,
        border: '1px solid #e6e6e6',
        bgcolor: '#fafafa',
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#2e7d321f',
          color: '#2e7d32',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: '#8f8f8f',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '0.68rem',
            fontWeight: 600,
            display: 'block',
            mb: 0.4,
          }}
        >
          {label}
        </Typography>
        {pill ? (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.4,
              borderRadius: 5,
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#fff',
              bgcolor: accentColor || '#757575',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
            }}
          >
            {value}
          </Box>
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: '#212121',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  </Grid>
);

// Small summary card showing how much of the permit's validity window is
// left, colored the same way as the header chips (green / orange / red).
const ValidityStatusBar = ({ permit, dateFormat }) => {
  const { t } = useTranslation();
  if (!permit.valid_to || ['cancelled', 'rejected'].includes(permit.status)) return null;

  const remainingDays = Math.ceil((new Date(permit.valid_to) - new Date()) / (1000 * 60 * 60 * 24));
  const totalDays = permit.valid_from
    ? Math.max(1, Math.ceil((new Date(permit.valid_to) - new Date(permit.valid_from)) / (1000 * 60 * 60 * 24)))
    : null;

  const isExpired = remainingDays < 0;
  const barColor = isExpired ? '#e53935' : remainingDays <= 7 ? '#e53935' : remainingDays <= 30 ? '#ff9800' : '#2e7d32';
  const percentage = isExpired
    ? 100
    : totalDays
      ? Math.max(2, Math.min(100, (remainingDays / totalDays) * 100))
      : 0;

  const statusText = isExpired
    ? t('permitDetails.permitExpiredDaysAgo', { count: Math.abs(remainingDays) })
    : remainingDays === 0
      ? t('permitDetails.permitExpiresToday')
      : t('permitDetails.daysRemainingValue', { count: remainingDays });

  return (
    <Grid item xs={12}>
      <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e6e6e6', bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: '#8f8f8f', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem', fontWeight: 600 }}
          >
            {t('permitDetails.daysRemainingLabel')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
            {formatDate(permit.valid_from, dateFormat)} — {formatDate(permit.valid_to, dateFormat)}
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: barColor, mb: 1 }}>
          {statusText}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: '#e3e3e3',
            '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 4 },
          }}
        />
      </Box>
    </Grid>
  );
};

const PermitDetails = () => {
  const { t } = useTranslation();
  const { permitId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgContext = useOrg();
  const orgPrefix = `/${(orgContext?.orgCode || localStorage.getItem('orgCode') || 'rta').toLowerCase()}`;
  const dateFormat = orgContext?.org?.settings?.date_format || 'DD/MM/YYYY';
  const [permit, setPermit] = useState(null);
  const [history, setHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [chalans, setChalans] = useState([]);
  const [loadingChalans, setLoadingChalans] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [detailedChangesDialog, setDetailedChangesDialog] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [reapplyDialogOpen, setReapplyDialogOpen] = useState(false);
  const [reapplyLoading, setReapplyLoading] = useState(false);
  const [newValidFrom, setNewValidFrom] = useState('');
  const [newValidTo, setNewValidTo] = useState('');
  const [reapplyError, setReapplyError] = useState('');

  // Forward permit state
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [forwardNotes, setForwardNotes] = useState('');
  const [forwardLoading, setForwardLoading] = useState(false);
  const [forwardError, setForwardError] = useState('');
  const [forwardSuccess, setForwardSuccess] = useState('');
  // Backward (send back) state
  const [backwardLoading, setBackwardLoading] = useState(false);
  const [backwardError, setBackwardError] = useState('');

  // Reject permit state (rejectReason/Loading/Error used inside the forward dialog)
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  // Inactive permit state
  const [inactiveDialogOpen, setInactiveDialogOpen] = useState(false);
  const [inactiveReason, setInactiveReason] = useState('');
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [inactiveError, setInactiveError] = useState('');

  // RFID tag state
  const [rfidHistory, setRfidHistory] = useState([]);
  const [rfidActiveTagUid, setRfidActiveTagUid] = useState(null);
  const [rfidDialogOpen, setRfidDialogOpen] = useState(false);
  const [rfidTagInput, setRfidTagInput] = useState('');
  const [rfidLoading, setRfidLoading] = useState(false);
  const [rfidError, setRfidError] = useState('');
  const [rfidSuccess, setRfidSuccess] = useState('');
  const [rfidGunListening, setRfidGunListening] = useState(false);
  const [rfidActionLoading, setRfidActionLoading] = useState(false);
  const rfidInputRef = useRef(null);

  const fetchPermitDetails = useCallback(async () => {
    try {
      const response = await apiClient.get(`/permits/${permitId}/`);
      setPermit(response.data);
      setError('');
      setAccessDenied(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403 || status === 404) {
        setAccessDenied(true);
      } else {
        setError(t('permitDetails.loadFailed'));
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [permitId, t]);

  const fetchPermitHistory = useCallback(async () => {
    try {
      const response = await apiClient.get(`/permits/${permitId}/history/`);
      setHistory(response.data.history);
    } catch (err) {
      console.error('Failed to load permit history', err);
    }
  }, [permitId]);

  const fetchPermitDocuments = useCallback(async () => {
    try {
      const response = await apiClient.get(`/permit-documents/?permit_id=${permitId}`);
      setDocuments(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  }, [permitId]);

  const fetchPermitChalans = useCallback(async () => {
    setLoadingChalans(true);
    try {
      const response = await apiClient.get(`/chalans/?permit=${permitId}`);
      setChalans(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load chalans:', err);
    } finally {
      setLoadingChalans(false);
    }
  }, [permitId]);

  const fetchRfidHistory = useCallback(async () => {
    try {
      const response = await apiClient.get(`/permits/${permitId}/rfid_history/`);
      setRfidHistory(response.data?.history || []);
      setRfidActiveTagUid(response.data?.active_tag_uid || null);
    } catch (err) {
      console.error('Failed to load RFID history', err);
    }
  }, [permitId]);

  useEffect(() => {
    fetchPermitDetails();
    fetchPermitHistory();
    fetchPermitDocuments();
    fetchPermitChalans();
    fetchRfidHistory();
  }, [fetchPermitDetails, fetchPermitHistory, fetchPermitDocuments, fetchPermitChalans, fetchRfidHistory]);

  const handleReapplyClick = () => {
    // Set default dates
    const today = new Date().toISOString().split('T')[0];
    setNewValidFrom(today);
    // Default to 1 year from today
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    setNewValidTo(nextYear.toISOString().split('T')[0]);
    setReapplyError('');
    setReapplyDialogOpen(true);
  };

  const handleReapplySubmit = async () => {
    if (!newValidTo) {
      setReapplyError(t('permitDetails.validToRequired'));
      return;
    }

    if (new Date(newValidFrom) >= new Date(newValidTo)) {
      setReapplyError(t('permitDetails.validToAfterFrom'));
      return;
    }

    setReapplyLoading(true);
    try {
      // Navigate to NewPermit wizard with the expired permit data
      setReapplyDialogOpen(false);
      navigate(`${orgPrefix}/new-permit?renewalPermitId=${permitId}&validFrom=${newValidFrom}&validTo=${newValidTo}`);
    } catch (err) {
      console.error('Failed to navigate to renewal permit:', err);
      setReapplyError(t('permitDetails.renewalStartFailed'));
    } finally {
      setReapplyLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const canEditPermit = () => {
    if (!user || !permit) return false;

    // Terminal statuses — nobody edits a closed permit
    if (['cancelled', 'expired', 'rejected'].includes(permit.status)) return false;

    // Admins can always edit
    if (user.is_staff) return true;

    // Must have permit_update feature
    if (!hasFeature(user, 'permit_update')) return false;

    // Permit must be assigned to a role
    if (!permit.assigned_to) return false;

    // User's role must currently hold the permit (case-insensitive match)
    const userRole = (user.role?.name || user.role || '').toLowerCase().trim();
    const assignedRole = (permit.assigned_to_role || '').toLowerCase().trim();

    // If role names are available, enforce the match
    if (userRole && assignedRole) return userRole === assignedRole;

    // assigned_to exists but role name is missing from one side — allow with feature
    return true;
  };

  const canSuspendPermit = () => {
    if (!user || !permit) return false;
    if (user.is_staff) return true;
    return hasFeature(user, 'permit_update');
  };

  const canDeletePermit = () => {
    if (!user || !permit) return false;
    if (user.is_staff) return true;
    return hasFeature(user, 'permit_delete');
  };

  const handleDeletePermit = async () => {
    if (!window.confirm(t('permitDetails.confirmDeleteMessage', { number: permit.permit_number }))) return;
    try {
      await apiClient.delete(`/permits/${permitId}/`);
      navigate(`${orgPrefix}/permits`);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || t('permitDetails.deleteFailed'));
    }
  };

  const canForwardPermit = () => {
    if (!user || !permit) return false;
    if (['cancelled', 'expired', 'rejected'].includes(permit.status)) return false;
    // Staff or users with forwarding_update can act on any permit
    if (user.is_staff || hasFeature(user, 'forwarding_update')) return true;
    // Others: role must currently hold the permit
    if (!permit.assigned_to) return false;
    const userRole = (user.role?.name || user.role || '').toLowerCase().trim();
    const assignedRole = (permit.assigned_to_role || '').toLowerCase().trim();
    return !!(userRole && assignedRole && userRole === assignedRole);
  };

  const canAssignRfid = () => {
    if (!user || !permit) return false;
    // A rejected/cancelled/expired/suspended permit isn't valid for road use,
    // so it shouldn't be getting a (new) active RFID tag either.
    if (['cancelled', 'expired', 'rejected', 'inactive'].includes(permit.status)) return false;
    if (user.is_staff) return true;
    // rfid_create covers the first tag on a permit, rfid_update covers
    // replacing an already-active one - either is enough to see the button.
    return hasFeature(user, 'rfid_create') || hasFeature(user, 'rfid_update');
  };

  const canResubmitPermit = () => {
    if (!user || !permit) return false;
    if (permit.status !== 'cancelled') return false;
    // Only the permit owner or staff can resubmit
    return user.is_staff || permit.user_owner === user.id;
  };

  const handleForwardSubmit = async () => {
    setForwardLoading(true);
    setForwardError('');
    setForwardSuccess('');
    try {
      const res = await apiClient.post(`/permits/${permitId}/forward/`, { notes: forwardNotes });
      const toRole = res.data.forwarded_to;
      setForwardSuccess(
        t('permitDetails.forwardedToMessage', { role: (toRole.role_display || toRole.role || '').replace(/_/g, ' ') })
      );
      setForwardDialogOpen(false);
      setForwardNotes('');
      fetchPermitDetails();
      fetchPermitHistory();
    } catch (err) {
      setForwardError(err.response?.data?.error || t('permitDetails.forwardFailed'));
    } finally {
      setForwardLoading(false);
    }
  };

  const handleBackwardSubmit = async () => {
    setBackwardLoading(true);
    setBackwardError('');
    try {
      const res = await apiClient.post(`/permits/${permitId}/backward/`, { notes: forwardNotes });
      const toRole = res.data.sent_back_to;
      setForwardDialogOpen(false);
      setForwardNotes('');
      fetchPermitDetails();
      fetchPermitHistory();
      setForwardSuccess(
        t('permitDetails.sentBackToMessage', { role: (toRole.role_display || toRole.role || '').replace(/_/g, ' ') })
      );
    } catch (err) {
      setBackwardError(err.response?.data?.error || t('permitDetails.backwardFailed'));
    } finally {
      setBackwardLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    setRejectLoading(true);
    setRejectError('');
    try {
      await apiClient.post(`/permits/${permitId}/reject/`, { reason: rejectReason });
      setRejectReason('');
      fetchPermitDetails();
      fetchPermitHistory();
    } catch (err) {
      setRejectError(err.response?.data?.error || t('permitDetails.rejectFailed'));
    } finally {
      setRejectLoading(false);
    }
  };

  const handleStartGunScan = () => {
    setRfidError('');
    setRfidSuccess('');
    setRfidTagInput('');
    setRfidGunListening(true);
    setTimeout(() => rfidInputRef.current?.focus(), 0);
  };

  const handleAssignRfidSubmit = async () => {
    if (!rfidTagInput.trim()) {
      setRfidError(t('permitDetails.rfidTagRequired'));
      return;
    }
    setRfidLoading(true);
    setRfidError('');
    setRfidSuccess('');
    try {
      const res = await apiClient.post(`/permits/${permitId}/assign_rfid/`, { tag_uid: rfidTagInput.trim() });
      setRfidSuccess(res.data.detail || t('permitDetails.rfidAssignSuccess'));
      setRfidTagInput('');
      setRfidGunListening(false);
      fetchRfidHistory();
    } catch (err) {
      setRfidError(err.response?.data?.error || t('permitDetails.rfidAssignFailed'));
    } finally {
      setRfidLoading(false);
    }
  };

  const canDeleteRfid = () => {
    if (!user || !permit) return false;
    if (user.is_staff) return true;
    return hasFeature(user, 'rfid_delete');
  };

  const handleDeactivateRfid = async () => {
    if (!window.confirm(t('permitDetails.confirmDeactivateRfid'))) return;
    setRfidActionLoading(true);
    setRfidError('');
    setRfidSuccess('');
    try {
      const res = await apiClient.post(`/permits/${permitId}/deactivate_rfid/`);
      setRfidSuccess(res.data.detail || t('permitDetails.rfidDeactivateSuccess'));
      fetchRfidHistory();
    } catch (err) {
      setRfidError(err.response?.data?.error || t('permitDetails.rfidDeactivateFailed'));
    } finally {
      setRfidActionLoading(false);
    }
  };

  const handleInactiveSubmit = async () => {
    setInactiveLoading(true);
    setInactiveError('');
    try {
      await apiClient.post(`/permits/${permitId}/set_inactive/`, { reason: inactiveReason });
      setInactiveDialogOpen(false);
      setInactiveReason('');
      fetchPermitDetails();
      fetchPermitHistory();
    } catch (err) {
      setInactiveError(err.response?.data?.error || t('permitDetails.statusUpdateFailed'));
    } finally {
      setInactiveLoading(false);
    }
  };

  // PDF body text below stays in English intentionally — jsPDF's built-in fonts can't render Urdu glyphs.
  const downloadReportPDF = async () => {
    if (!permit) return;
    setPdfLoading(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 15;
      const contentW = pageW - margin * 2;
      const BLUE = [21, 101, 192];

      const fmt = (val) => (val !== null && val !== undefined && val !== '') ? String(val) : '—';
      const fmtDate = (val) => {
        if (!val) return '—';
        try { return new Date(val).toLocaleDateString(); } catch { return String(val); }
      };
      const authorityFull = permit.authority === 'PTA'
        ? 'Provincial Transport Authority'
        : permit.authority === 'RTA'
          ? 'Regional Transport Authority'
          : fmt(permit.authority);

      // ── HEADER BANNER ──────────────────────────────────────────────
      doc.setFillColor(...BLUE);
      doc.rect(margin, 10, contentW, 22, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(authorityFull, pageW / 2, 19, { align: 'center' });
      doc.setFontSize(10);
      doc.text('PERMIT DOCUMENT', pageW / 2, 26, { align: 'center' });
      doc.setTextColor(0, 0, 0);

      // meta row
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Permit #: ${permit.permit_number}`, margin, 38);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageW - margin, 38, { align: 'right' });
      const statusColors = { active: [46, 125, 50], cancelled: [198, 40, 40], expired: [230, 81, 0], pending: [245, 124, 0], rejected: [198, 40, 40] };
      doc.setTextColor(...(statusColors[permit.status] || BLUE));
      doc.setFont('helvetica', 'bold');
      doc.text(`Status: ${getStatusLabel(permit.status)?.toUpperCase()}`, margin, 44);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');

      let y = 50;

      const sectionHeader = (title) => {
        if (y > 265) { doc.addPage(); y = 15; }
        doc.setFillColor(...BLUE);
        doc.rect(margin, y, contentW, 7, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin + 3, y + 5);
        doc.setTextColor(0, 0, 0);
        y += 9;
      };

      const addTable = (body) => {
        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [],
          body,
          styles: { fontSize: 9, cellPadding: 2.5 },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55, fillColor: [235, 245, 255] } },
          theme: 'grid',
          tableLineColor: [200, 220, 240],
          tableLineWidth: 0.1,
        });
        y = doc.lastAutoTable.finalY + 5;
      };

      // ── BASIC INFORMATION ──────────────────────────────────────────
      sectionHeader('BASIC INFORMATION');
      addTable([
        ['Permit Number', fmt(permit.permit_number)],
        ['Authority', authorityFull],
        ['Permit Type', typeof permit.permit_type === 'object' ? fmt(permit.permit_type?.name) : fmt(permit.permit_type)],
        ['Status', getStatusLabel(permit.status)?.toUpperCase() || '—'],
      ]);

      // ── VEHICLE INFORMATION ────────────────────────────────────────
      sectionHeader('VEHICLE INFORMATION');
      addTable([
        ['Vehicle Number', fmt(permit.vehicle_number)],
        ['Vehicle Type', typeof permit.vehicle_type === 'object' ? fmt(permit.vehicle_type?.name) : fmt(permit.vehicle_type)],
        ['Make', fmt(permit.vehicle_make)],
        ['Model', fmt(permit.vehicle_model)],
        ['Year', fmt(permit.vehicle_year)],
        ['Capacity', permit.vehicle_capacity ? `${permit.vehicle_capacity} passengers` : '—'],
      ]);

      // ── OWNER INFORMATION ──────────────────────────────────────────
      sectionHeader('OWNER INFORMATION');
      addTable([
        ['Owner Name', fmt(permit.owner_name)],
        ['Email', fmt(permit.owner_email)],
        ['Phone', fmt(permit.owner_phone)],
        ['CNIC', fmt(permit.owner_cnic)],
        ['Address', fmt(permit.owner_address)],
      ]);

      // ── PERMIT VALIDITY ────────────────────────────────────────────
      sectionHeader('PERMIT VALIDITY');
      addTable([
        ['Issued Date', fmtDate(permit.issued_date)],
        ['Valid From', fmtDate(permit.valid_from)],
        ['Valid To', fmtDate(permit.valid_to)],
        ['Days Remaining', (permit.days_remaining !== null && permit.days_remaining !== undefined) ? `${permit.days_remaining} days` : '—'],
        ['Last Modified', fmtDate(permit.last_modified)],
      ]);

      // ── ROUTES & RESTRICTIONS ──────────────────────────────────────
      if (permit.approved_routes || permit.restrictions) {
        sectionHeader('ROUTES & RESTRICTIONS');
        addTable([
          ['Approved Routes', fmt(permit.approved_routes)],
          ['Restrictions', fmt(permit.restrictions)],
        ]);
      }

      // ── DESCRIPTION & REMARKS ──────────────────────────────────────
      if (permit.description || permit.remarks) {
        sectionHeader('DESCRIPTION & REMARKS');
        addTable([
          ['Description', fmt(permit.description)],
          ['Remarks', fmt(permit.remarks)],
        ]);
      }

      // ── ASSIGNMENT INFO (staff only) ─────────────────────────────
      const isEndUserPdf = !hasFeature(user, 'employee') && !user?.is_staff;
      if (!isEndUserPdf && (permit.assigned_to_role || permit.assigned_to_full_name)) {
        sectionHeader('ASSIGNMENT INFORMATION');
        addTable([
          ['Assigned Role', fmt(permit.assigned_to_full_name || permit.assigned_to_username || permit.assigned_to_role)],
          ['Assigned At', fmtDate(permit.assigned_at)],
          ['Assigned By', fmt(permit.assigned_by)],
        ]);
      }

      // ── ATTACHMENTS INDEX ──────────────────────────────────────────
      if (documents && documents.length > 0) {
        doc.addPage();
        y = 15;
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...BLUE);
        doc.text('ATTACHMENTS', pageW / 2, y, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        y += 8;

        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [['#', 'Filename', 'Type', 'Size', 'Uploaded By', 'Verified']],
          body: documents.map((d, i) => [
            String(i + 1),
            d.filename || '—',
            d.document_type || '—',
            d.file_size_display || '—',
            d.uploaded_by || '—',
            d.is_verified ? 'Yes' : 'No',
          ]),
          styles: { fontSize: 8.5, cellPadding: 2.5 },
          headStyles: { fillColor: BLUE, textColor: 255 },
          theme: 'striped',
        });
        y = doc.lastAutoTable.finalY + 5;

        // ── EMBED IMAGE ATTACHMENTS ──────────────────────────────────
        const token = localStorage.getItem('token');
        const orgCode = localStorage.getItem('orgCode');
        for (const docItem of documents) {
          const rawExt = (docItem.filename || docItem.file || '').split('.').pop().split('?')[0].toLowerCase();
          if (!['jpg', 'jpeg', 'png'].includes(rawExt)) continue;
          try {
            const fileUrl = docItem.file?.startsWith('http') ? docItem.file : `${BACKEND_BASE_URL}${docItem.file}`;
            const headers = {};
            if (token) headers['Authorization'] = `Token ${token}`;
            if (orgCode) headers['X-Tenant-Code'] = orgCode;
            const res = await fetch(fileUrl, { headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const dataURL = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            const imgDims = await new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
              img.onerror = () => resolve({ w: 1, h: 1 });
              img.src = dataURL;
            });
            doc.addPage();
            const pageH = doc.internal.pageSize.getHeight();
            const imgMaxW = pageW - margin * 2;
            const imgMaxH = pageH - 38;
            const ratio = Math.min(imgMaxW / imgDims.w, imgMaxH / imgDims.h);
            const imgW = imgDims.w * ratio;
            const imgH = imgDims.h * ratio;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...BLUE);
            doc.text(`Attachment: ${docItem.filename}`, margin, 12);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(`Type: ${docItem.document_type || '—'}  |  Size: ${docItem.file_size_display || '—'}`, margin, 18);
            doc.setTextColor(0, 0, 0);
            doc.addImage(dataURL, rawExt === 'png' ? 'PNG' : 'JPEG', margin, 22, imgW, imgH, undefined, 'FAST');
          } catch (e) {
            console.warn(`Could not embed attachment "${docItem.filename}":`, e.message);
          }
        }
      }

      doc.save(`permit_${permit.permit_number}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert(t('permitDetails.pdfGenerateFailed'));
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return <PageLoader variant="detail" />;
  }

  if (accessDenied) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="success">{t('permitDetails.accessDeniedMessage')}</Alert>
        <Button onClick={() => navigate(`${orgPrefix}/permits`)} sx={{ mt: 2 }}>
          {t('permitDetails.backToPermits')}
        </Button>
      </Box>
    );
  }

  if (error || !permit) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || t('permitDetails.permitNotFound')}</Alert>
        <Button onClick={() => navigate(`${orgPrefix}/permits`)} sx={{ mt: 2 }}>
          {t('permitDetails.backToPermits')}
        </Button>
      </Box>
    );
  }

  const isEmployee = hasFeature(user, 'employee') || user?.is_staff;
  const daysUntilExpiry = (permit.status === 'active' && permit.valid_to)
    ? Math.ceil((new Date(permit.valid_to) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  // Same status -> color mapping as PermitSearch's result card, so a permit
  // reads consistently whether you found it via search or opened it directly.
  const accentColor = (() => {
    if (permit.status === 'expired' || permit.status === 'cancelled' || permit.status === 'rejected') return '#f44336';
    if (permit.status === 'pending') return '#ff9800';
    if (permit.status === 'active') {
      if (daysUntilExpiry !== null && daysUntilExpiry < 0) return '#f44336';
      if (daysUntilExpiry !== null && daysUntilExpiry < 30) return '#ff9800';
      return '#4caf50';
    }
    return '#2196f3';
  })();

  // Assign/replace and deactivate hit the same permit-scoped RFID state on
  // the backend, so treat them as one busy flag here too - otherwise a user
  // could fire a deactivate and an assign for the same permit at once from
  // this same dialog.
  const rfidBusy = rfidLoading || rfidActionLoading;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          {t('permitDetails.pageTitle', { number: permit.permit_number })}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={pdfLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={downloadReportPDF}
            disabled={pdfLoading}
            sx={{
              bgcolor: '#1565c0',
              '&:hover': { bgcolor: '#0d47a1' },
              '& .MuiButton-startIcon': { transition: 'transform 0.2s ease-out' },
              '&:hover .MuiButton-startIcon': { transform: 'translateY(2px)' },
            }}
          >
            {pdfLoading ? t('permitDetails.generating') : t('permitDetails.downloadPdf')}
          </Button>
          {permit.status === 'expired' && (
            <Button
              variant="contained"
              size="small"
              sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
              onClick={handleReapplyClick}
            >
              {t('permitDetails.reapplyForExpired')}
            </Button>
          )}
          {canResubmitPermit() && (
            <Button
              variant="contained"
              size="small"
              sx={{ bgcolor: '#e53935', '&:hover': { bgcolor: '#c62828' } }}
              onClick={handleReapplyClick}
            >
              {t('permitDetails.resubmit')}
            </Button>
          )}
          {canForwardPermit() && (
            <Button
              variant="contained"
              size="small"
              startIcon={<ForwardIcon />}
              onClick={() => { setForwardError(''); setForwardNotes(''); setRejectReason(''); setRejectError(''); setBackwardError(''); setForwardDialogOpen(true); }}
              sx={{
                bgcolor: '#7b1fa2',
                '&:hover': { bgcolor: '#6a1b9a' },
                '& .MuiButton-startIcon': { transition: 'transform 0.2s ease-out' },
                '&:hover .MuiButton-startIcon': { transform: 'translateX(2px)' },
              }}
            >
              {t('permitDetails.forwardRejectBtn')}
            </Button>
          )}
          {canAssignRfid() && (
            <Button
              variant="contained"
              size="small"
              startIcon={<NfcIcon />}
              onClick={() => { setRfidError(''); setRfidSuccess(''); setRfidTagInput(''); setRfidGunListening(false); setRfidDialogOpen(true); }}
              sx={{ bgcolor: '#00695c', '&:hover': { bgcolor: '#004d40' } }}
            >
              {rfidActiveTagUid ? t('permitDetails.replaceRfidTitle') : t('permitDetails.assignRfidBtn')}
            </Button>
          )}

          <Box sx={{ width: '1px', height: 28, bgcolor: '#e0e0e0', mx: 0.5 }} />

          {['active', 'inactive'].includes(permit.status) && (
            <PermissionTooltip title={!canSuspendPermit() ? t('permitDetails.noSuspendPermission') : ''}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!canSuspendPermit()}
                  onClick={() => { setInactiveError(''); setInactiveReason(''); setInactiveDialogOpen(true); }}
                  sx={permit.status === 'active'
                    ? { borderColor: '#f57f17', color: '#f57f17', '&:hover': { bgcolor: '#fff8e1', borderColor: '#e65100' } }
                    : { borderColor: '#2e7d32', color: '#2e7d32', '&:hover': { bgcolor: '#e8f5e9', borderColor: '#1b5e20' } }
                  }
                >
                  {permit.status === 'active' ? t('permitDetails.suspend') : t('permitDetails.setActive')}
                </Button>
              </span>
            </PermissionTooltip>
          )}
          <PermissionTooltip
            title={
              !canEditPermit()
                ? ['cancelled', 'expired', 'rejected'].includes(permit?.status)
                  ? t('permitDetails.cannotEditStatus', { status: permit.status })
                  : !hasFeature(user, 'permit_update')
                    ? t('permitDetails.noUpdatePermission')
                    : t('permitDetails.assignedToRoleOnly', { role: permit?.assigned_to_role || t('permitDetails.anotherRoleFallback') })
                : ''
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate(`${orgPrefix}/permit-edit/${permitId}`)}
                disabled={!canEditPermit()}
                sx={canEditPermit()
                  ? { borderColor: '#1976d2', color: '#1976d2', '&:hover': { bgcolor: '#e3f2fd', borderColor: '#0d47a1' } }
                  : { bgcolor: '#fff', color: '#aaa', borderColor: '#ddd' }
                }
              >
                {t('common.edit')}
              </Button>
            </span>
          </PermissionTooltip>
          {canDeletePermit() && (
            <PermissionTooltip title="">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeletePermit}
                  sx={{ borderColor: '#d32f2f', color: '#d32f2f', '&:hover': { bgcolor: '#fdecea', borderColor: '#b71c1c' } }}
                >
                  {t('common.delete')}
                </Button>
              </span>
            </PermissionTooltip>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<BackwardIcon />}
            onClick={() => navigate(`${orgPrefix}/permits`)}
            sx={{
              ml: 1,
              borderColor: '#bdbdbd',
              color: '#616161',
              '&:hover': { bgcolor: '#f5f5f5', borderColor: '#9e9e9e' },
              '& .MuiButton-startIcon': { transition: 'transform 0.2s ease-out' },
              '&:hover .MuiButton-startIcon': { transform: 'translateX(-2px)' },
            }}
          >
            {t('common.back')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {forwardSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setForwardSuccess('')}>
          {forwardSuccess}
        </Alert>
      )}

      {/* Status Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          label={t(`common.${permit.status}`, { defaultValue: permit.status }).toUpperCase()}
          color={permit.status === 'active' ? 'success' : 'default'}
          variant="filled"
        />
        <Chip
          label={t('permitDetails.authorityChip', { authority: permit.authority })}
          variant="outlined"
        />
        {daysUntilExpiry && (
          <Chip
            label={t('permitDetails.expiresInDays', { count: daysUntilExpiry })}
            color={daysUntilExpiry <= 7 ? 'error' : daysUntilExpiry <= 30 ? 'warning' : 'default'}
            variant="outlined"
          />
        )}
      </Box>

      {/* Workflow Progress Bar */}
      {permit.workflow_progress && (
        <WorkflowProgressBar
          progress={permit.workflow_progress}
          permitStatus={permit.status}
          isEndUser={!hasFeature(user, 'employee') && !user?.is_staff}
        />
      )}

      {/* Tabs */}
      <Paper sx={{ borderLeft: `5px solid ${accentColor}` }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: '1px solid #ddd',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Tab label={t('permitEdit.basicInfoTab')} icon={<InfoIcon />} />
          <Tab label={t('permitEdit.vehicleDetailsTab')} icon={<VehicleIcon />} />
          <Tab label={t('permitDetails.ownerInfoTab')} icon={<PersonIcon />} />
          <Tab label={t('permitDetails.additionalDetailsTab')} icon={<DescriptionIcon />} />
          {isEmployee && <Tab label={t('permitDetails.historyTab')} icon={<HistoryIcon />} />}
          <Tab label={t('permitEdit.documentsTab')} icon={<AssignmentIcon />} />
          <Tab label={t('permitEdit.chalansTab', { count: chalans.length })} icon={<AssignmentIcon />} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Basic Information Tab */}
          {tabValue === 0 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {t('permitDetails.basicInfoSectionTitle')}
                </Typography>
              </Grid>
              <DetailCard
                icon={PermitNumberIcon}
                label={t('permitDetails.permitNumberField')}
                value={permit.permit_number}
              />
              <DetailCard
                icon={AuthorityIcon}
                label={t('permitEdit.authority')}
                value={t('permitDetails.authorityValue', { code: permit.authority })}
              />
              <DetailCard
                icon={CategoryIcon}
                label={t('permitEdit.permitType')}
                value={permit.permit_type && typeof permit.permit_type === 'object'
                  ? permit.permit_type.name
                  : permit.permit_type}
              />
              <DetailCard
                icon={InfoIcon}
                label={t('permitEdit.status')}
                value={t(`common.${permit.status}`, { defaultValue: permit.status })}
                accentColor={accentColor}
                pill
              />
              <DetailCard
                icon={EventIcon}
                label={t('permitDetails.issuedDateField')}
                value={formatDate(permit.issued_date, dateFormat)}
                sm={3}
              />
              <DetailCard
                icon={UpdateIcon}
                label={t('permitDetails.lastModifiedField')}
                value={formatDate(permit.last_modified, dateFormat)}
                sm={3}
              />
              <DetailCard
                icon={EventAvailableIcon}
                label={t('permitDetails.validFromField')}
                value={formatDate(permit.valid_from, dateFormat)}
                sm={3}
              />
              <DetailCard
                icon={EventBusyIcon}
                label={t('permitDetails.validToField')}
                value={formatDate(permit.valid_to, dateFormat)}
                sm={3}
              />

              <ValidityStatusBar permit={permit} dateFormat={dateFormat} />

              {/* Permit Renewal Status and Chain */}
              {permit.has_previous_permits && (
                <>
                  <Grid item xs={12}>
                    <Card sx={{ backgroundColor: '#fff3cd', borderLeft: '4px solid #ff9800' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#ff6f00' }}>
                          {t('permitDetails.renewalPermitTitle')}
                        </Typography>
                        <Typography variant="body2">
                          {t('permitDetails.renewalPermitDesc')}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          {permit.permit_chain && permit.permit_chain.length > 0 ? (
                            <Box>
                              {permit.permit_chain.map((previousPermit, index) => (
                                <Box key={index} sx={{ mb: 1, p: 1, backgroundColor: '#fff', borderRadius: '4px' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {index === 0 ? t('permitDetails.currentLabel') : t('permitDetails.previousIndexedLabel', { index })} {previousPermit.permit_number}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.5 }}>
                                    {t('permitDetails.statusColonLabel')} <Chip label={t(`common.${previousPermit.status}`, { defaultValue: previousPermit.status })} size="small" sx={{ ml: 1 }} />
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                                    {t('permitDetails.validRangeLabel', { from: previousPermit.valid_from, to: previousPermit.valid_to })}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : permit.previous_permits && permit.previous_permits.length > 0 ? (
                            <Box>
                              {permit.previous_permits.map((previousPermit, index) => (
                                <Box key={index} sx={{ mb: 1, p: 1, backgroundColor: '#fff', borderRadius: '4px' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('permitDetails.previousLabel')} {previousPermit.permit_number}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.5 }}>
                                    {t('permitDetails.statusColonLabel')} <Chip label={t(`common.${previousPermit.status}`, { defaultValue: previousPermit.status })} size="small" sx={{ ml: 1 }} />
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                                    {t('permitDetails.validRangeLabel', { from: previousPermit.valid_from, to: previousPermit.valid_to })}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption">{t('permitDetails.noChainAvailable')}</Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {/* Vehicle Details Tab */}
          {tabValue === 1 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {t('permitSearch.vehicleInfo')}
                </Typography>
              </Grid>
              <DetailCard
                icon={VehicleIcon}
                label={t('permitDetails.vehicleNumberField')}
                value={permit.vehicle_number}
              />
              <DetailCard
                icon={CategoryIcon}
                label={t('permitEdit.vehicleType')}
                value={permit.vehicle_type && typeof permit.vehicle_type === 'object'
                  ? permit.vehicle_type.name
                  : permit.vehicle_type || t('permitSearch.na')}
              />
              <DetailCard
                icon={MakeIcon}
                label={t('permitDetails.makeField')}
                value={permit.vehicle_make || t('permitSearch.na')}
              />
              <DetailCard
                icon={VehicleIcon}
                label={t('permitDetails.modelField')}
                value={permit.vehicle_model || t('permitSearch.na')}
              />
              <DetailCard
                icon={CalendarIcon}
                label={t('permitDetails.yearField')}
                value={permit.vehicle_year || t('permitSearch.na')}
              />
              <DetailCard
                icon={SeatIcon}
                label={t('permitDetails.capacitySeatingField')}
                value={permit.vehicle_capacity ? t('permitEdit.seatsUnit', { count: permit.vehicle_capacity }) : t('permitSearch.na')}
              />
            </Grid>
          )}

          {/* Owner Information Tab */}
          {tabValue === 2 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {t('permitSearch.ownerInfo')}
                </Typography>
              </Grid>
              <DetailCard
                icon={PersonIcon}
                label={t('permitDetails.nameField')}
                value={permit.owner_name}
              />
              <DetailCard
                icon={CnicIcon}
                label={t('permitDetails.cnicField')}
                value={permit.owner_cnic || t('permitSearch.na')}
              />
              <DetailCard
                icon={EmailIcon}
                label={t('permitDetails.emailField')}
                value={permit.owner_email}
              />
              <DetailCard
                icon={PhoneIcon}
                label={t('permitDetails.phoneField')}
                value={permit.owner_phone}
              />
              <DetailCard
                icon={AddressIcon}
                label={t('permitDetails.addressField')}
                value={permit.owner_address || t('permitSearch.na')}
                fullWidth
              />
            </Grid>
          )}

          {/* Additional Details Tab */}
          {tabValue === 3 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {t('permitDetails.additionalDetailsTab')}
                </Typography>
              </Grid>
              <DetailCard
                icon={DescriptionIcon}
                label={t('permitEdit.descriptionLabel')}
                value={permit.description || t('permitSearch.na')}
                fullWidth
              />
              <DetailCard
                icon={NotesIcon}
                label={t('permitEdit.remarksLabel')}
                value={permit.remarks || t('permitSearch.na')}
                fullWidth
              />
              <DetailCard
                icon={RouteIcon}
                label={t('permitEdit.approvedRoutesLabel')}
                value={permit.approved_routes || t('permitSearch.na')}
                fullWidth
              />
              <DetailCard
                icon={RestrictionsIcon}
                label={t('permitDetails.restrictionsField')}
                value={permit.restrictions || t('permitSearch.na')}
                fullWidth
              />
            </Grid>
          )}

          {/* History Tab */}
          {isEmployee && tabValue === 4 && (
            <Box>
              {(() => {
                return history.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell><strong>{t('reports.actionCol')}</strong></TableCell>
                          {isEmployee && <TableCell><strong>{t('permitDetails.performedByField')}</strong></TableCell>}
                          <TableCell><strong>{t('permitDetails.dateTimeField')}</strong></TableCell>
                          <TableCell><strong>{t('reports.notesCol')}</strong></TableCell>
                          {isEmployee && <TableCell><strong>{t('permitDetails.detailsField')}</strong></TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Chip
                                label={t(`permitDetails.historyAction${item.action.charAt(0).toUpperCase()}${item.action.slice(1)}`, { defaultValue: item.action })}
                                variant="outlined"
                                size="small"
                                color={
                                  item.action === 'created' ? 'success'
                                    : item.action === 'cancelled' ? 'error'
                                      : item.action === 'rejected' ? 'error'
                                        : item.action === 'assigned' ? 'primary'
                                          : item.action === 'forwarded' ? 'secondary'
                                            : item.action === 'activated' ? 'success'
                                              : item.action === 'renewed' ? 'info'
                                                : 'default'
                                }
                              />
                            </TableCell>
                            {isEmployee && <TableCell>{item.performed_by}</TableCell>}
                            <TableCell>
                              {new Date(item.timestamp).toLocaleString()}
                            </TableCell>
                            <TableCell>{item.notes}</TableCell>
                            {isEmployee && (
                              <TableCell>
                                {Object.keys(item.changes || {}).length > 0 ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      setSelectedChange(item.changes);
                                      setDetailedChangesDialog(true);
                                    }}
                                  >
                                    {t('permitDetails.viewChangesBtn')}
                                  </Button>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="textSecondary">{t('reports.noHistory')}</Typography>
                );
              })()}
            </Box>
          )}

          {/* Chalans Tab */}
          {tabValue === (isEmployee ? 6 : 5) && (
            <Box>
              {loadingChalans ? (
                <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
              ) : chalans.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>{t('permitDetails.chalanNumberCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitEdit.status')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.feeAmountCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.paidAmountCol')}</strong></TableCell>
                        <TableCell><strong>{t('reports.dateCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.paymentDateCol')}</strong></TableCell>
                        <TableCell><strong>{t('reports.actionCol')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chalans.map((chalan) => (
                        <TableRow key={chalan.id} hover>
                          <TableCell>{chalan.chalan_number}</TableCell>
                          <TableCell>
                            <Chip
                              label={t(`common.${chalan.status}`, { defaultValue: chalan.status }).toUpperCase()}
                              size="small"
                              color={
                                chalan.status === 'paid' ? 'success' :
                                  chalan.status === 'cancelled' ? 'error' :
                                    chalan.status === 'issued' ? 'info' : 'warning'
                              }
                            />
                          </TableCell>
                          <TableCell>{chalan.fees_amount}</TableCell>
                          <TableCell>{chalan.paid_amount}</TableCell>
                          <TableCell>{formatDate(chalan.issued_date, dateFormat)}</TableCell>
                          <TableCell>{chalan.payment_date ? formatDate(chalan.payment_date, dateFormat) : '—'}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`${orgPrefix}/chalans/${chalan.id}`, {
                                state: { returnTo: `${orgPrefix}/permit-details/${permitId}` },
                              })}
                            >
                              {t('common.view')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">{t('permitEdit.noChalans')}</Alert>
              )}
            </Box>
          )}

          {/* Documents Tab */}
          {tabValue === (isEmployee ? 5 : 4) && (
            <Box>
              {documents.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>{t('permitDetails.previewCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.documentTypeCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.filenameCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.uploadedByCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.uploadedDateCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.verifiedCol')}</strong></TableCell>
                        <TableCell><strong>{t('permitDetails.openCol')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documents.map((doc) => {
                        const docUrl = doc.file
                          ? (doc.file.startsWith('http') ? doc.file : `${BACKEND_BASE_URL}${doc.file}`)
                          : null;
                        const isImage = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'BMP'].includes(
                          (doc.file_extension || '').toUpperCase()
                        );
                        return (
                          <TableRow key={doc.id} hover>
                            <TableCell>
                              {docUrl && isImage ? (
                                <Box
                                  component="img"
                                  src={docUrl}
                                  alt={doc.filename}
                                  onClick={() => setPreviewDocument({ url: docUrl, filename: doc.filename })}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0',
                                    cursor: 'pointer',
                                  }}
                                />
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  {doc.file_extension || t('permitDetails.fileFallback')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{doc.document_type}</TableCell>
                            <TableCell>{doc.filename}</TableCell>
                            <TableCell>{doc.uploaded_by || t('permitSearch.na')}</TableCell>
                            <TableCell>
                              {formatDate(doc.uploaded_at, dateFormat)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={doc.is_verified ? t('permitDetails.verifiedChip') : t('permitDetails.notVerifiedChip')}
                                color={doc.is_verified ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {docUrl && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DownloadIcon />}
                                  component="a"
                                  href={docUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t('permitDetails.openCol')}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">{t('permitDetails.noDocumentsAttached')}</Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Changes Dialog */}
      <Dialog open={detailedChangesDialog} onClose={() => setDetailedChangesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('permitDetails.detailedChangesTitle')}</DialogTitle>
        <DialogContent dividers>
          {selectedChange && Object.keys(selectedChange).length > 0 ? (
            <Box>
              {Object.entries(selectedChange).map(([field, change]) => {
                const isOldNew = change !== null && typeof change === 'object' && ('old' in change || 'new' in change);
                return (
                  <Box key={field} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {field.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2 }}>
                      {isOldNew ? (
                        <>
                          <Typography variant="body2" color="error">
                            {change.old !== undefined && change.old !== null ? String(change.old) : t('permitDetails.emptyValue')}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">→</Typography>
                          <Typography variant="body2" color="success">
                            {change.new !== undefined && change.new !== null ? String(change.new) : t('permitDetails.emptyValue')}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="primary">
                          {String(change !== undefined && change !== null ? change : t('permitDetails.emptyValue'))}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography color="textSecondary">{t('permitDetails.noChangesToDisplay')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailedChangesDialog(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Document Image Preview Dialog */}
      <Dialog open={!!previewDocument} onClose={() => setPreviewDocument(null)} maxWidth="md">
        <DialogTitle>{previewDocument?.filename}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          {previewDocument && (
            <Box
              component="img"
              src={previewDocument.url}
              alt={previewDocument.filename}
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            component="a"
            href={previewDocument?.url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DownloadIcon />}
          >
            {t('permitDetails.openFullSize')}
          </Button>
          <Button onClick={() => setPreviewDocument(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Reapply / Resubmit Permit Dialog */}
      <Dialog open={reapplyDialogOpen} onClose={() => setReapplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {permit?.status === 'cancelled' ? t('permitDetails.resubmitRejectedTitle') : t('permitDetails.reapplyExpiredTitle')}
        </DialogTitle>
        <DialogContent dividers>
          {reapplyError && (
            <Alert severity="error" sx={{ mb: 2 }}>{reapplyError}</Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {permit?.status === 'cancelled'
                ? t('permitDetails.resubmitDesc', { number: permit?.permit_number })
                : t('permitDetails.reapplyDesc', { number: permit?.permit_number })}
            </Typography>
            <TextField
              label={t('permitDetails.validFromField')}
              type="date"
              value={newValidFrom}
              onChange={(e) => setNewValidFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label={t('permitDetails.validToField')}
              type="date"
              value={newValidTo}
              onChange={(e) => setNewValidTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <Alert severity="info">
              {t('permitDetails.newPermitPendingNotice')}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReapplyDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleReapplySubmit}
            variant="contained"
            disabled={reapplyLoading}
            sx={{ bgcolor: permit?.status === 'cancelled' ? '#e53935' : '#ff9800' }}
          >
            {reapplyLoading ? t('permitDetails.creatingBtn') : permit?.status === 'cancelled' ? t('permitDetails.resubmitPermitBtn') : t('permitDetails.createRenewalPermitBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forward / Reject Permit Dialog */}
      <Dialog open={forwardDialogOpen} onClose={() => !forwardLoading && !rejectLoading && !backwardLoading && setForwardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('permitDetails.forwardDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('permitDetails.forwardDialogDesc')}
          </Typography>
          {(!permit?.can_forward || !permit?.can_backward) && (
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {!permit?.can_backward && (
                <Alert severity="info" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                  {t('permitDetails.sendBackUnavailableNotice')}
                </Alert>
              )}
              {!permit?.can_forward && (
                <Alert severity="info" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                  {t('permitDetails.forwardUnavailableNotice')}
                </Alert>
              )}
            </Box>
          )}

          {/* Forward section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#7b1fa2' }}>{t('permitDetails.forwardSendBackSubtitle')}</Typography>
          {forwardError && (
            <Alert severity="error" sx={{ mb: 2 }}>{forwardError}</Alert>
          )}
          {backwardError && (
            <Alert severity="error" sx={{ mb: 2 }}>{backwardError}</Alert>
          )}
          {forwardSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>{forwardSuccess}</Alert>
          )}
          <TextField
            label={t('permitDetails.notesOptionalLabel')}
            fullWidth
            multiline
            rows={2}
            value={forwardNotes}
            onChange={e => setForwardNotes(e.target.value)}
            placeholder={t('permitDetails.notesPlaceholder')}
            disabled={forwardLoading || rejectLoading}
          />

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2, gap: 1 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            <Typography variant="caption" color="text.secondary">{t('permitDetails.orDivider')}</Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>

          {/* Reject section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#d32f2f' }}>{t('permitDetails.rejectSubtitle')}</Typography>
          <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
            {t('permitDetails.rejectingWarning')}
          </Alert>
          {rejectError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>{rejectError}</Alert>
          )}
          <TextField
            label={t('permitDetails.rejectReasonLabel')}
            fullWidth
            multiline
            rows={2}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder={t('permitDetails.rejectReasonPlaceholder')}
            disabled={forwardLoading || rejectLoading}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button onClick={() => setForwardDialogOpen(false)} disabled={forwardLoading || rejectLoading || backwardLoading}>{t('common.cancel')}</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={async () => {
                setRejectError('');
                await handleRejectSubmit();
                setForwardDialogOpen(false);
              }}
              disabled={forwardLoading || rejectLoading || backwardLoading}
              startIcon={rejectLoading ? <CircularProgress size={16} /> : null}
              sx={{ borderColor: '#d32f2f', color: '#d32f2f', '&:hover': { bgcolor: '#fdecea', borderColor: '#b71c1c' } }}
            >
              {rejectLoading ? t('permitDetails.rejectingBtn') : t('permitDetails.rejectBtn')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleBackwardSubmit}
              disabled={forwardLoading || rejectLoading || backwardLoading || !permit?.can_backward}
              startIcon={backwardLoading ? <CircularProgress size={16} /> : <BackwardIcon />}
              title={!permit?.can_backward ? t('permitDetails.firstStepTooltip') : ''}
              sx={{
                borderColor: permit?.can_backward ? '#e65100' : '#bbb',
                color: permit?.can_backward ? '#e65100' : '#bbb',
                '&:hover': { bgcolor: '#fff3e0', borderColor: '#bf360c' },
                '&.Mui-disabled': { borderColor: '#e0e0e0', color: '#bbb' },
              }}
            >
              {backwardLoading ? t('permitDetails.sendingBackBtn') : t('permitDetails.sendBackBtn')}
            </Button>
            <Button
              variant="contained"
              onClick={handleForwardSubmit}
              disabled={forwardLoading || rejectLoading || backwardLoading || !permit?.can_forward}
              startIcon={forwardLoading ? <CircularProgress size={16} /> : <ForwardIcon />}
              title={!permit?.can_forward ? t('permitDetails.finalStepTooltip') : ''}
              sx={{
                bgcolor: permit?.can_forward ? '#7b1fa2' : '#bbb',
                '&:hover': { bgcolor: '#6a1b9a' },
                '&.Mui-disabled': { bgcolor: '#e0e0e0' },
              }}
            >
              {forwardLoading ? t('permitDetails.forwardingBtn') : t('permitDetails.forwardBtn')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* RFID Tag Assignment Dialog */}
      <Dialog open={rfidDialogOpen} onClose={() => !rfidBusy && setRfidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#00695c' }}>
          {rfidActiveTagUid ? t('permitDetails.replaceRfidTitle') : t('permitDetails.assignRfidTitle')}
        </DialogTitle>
        <DialogContent>
          {rfidError && <Alert severity="error" sx={{ mb: 2 }}>{rfidError}</Alert>}
          {rfidSuccess && <Alert severity="success" sx={{ mb: 2 }}>{rfidSuccess}</Alert>}

          {rfidActiveTagUid && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('permitDetails.rfidCurrentActiveNotice', { tagUid: rfidActiveTagUid })}
            </Alert>
          )}

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {rfidActiveTagUid ? t('permitDetails.replaceRfidInstructions') : t('permitDetails.assignRfidInstructions')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1 }}>
            <TextField
              autoFocus
              inputRef={rfidInputRef}
              label={t('permitDetails.rfidTagLabel')}
              fullWidth
              value={rfidTagInput}
              onChange={e => setRfidTagInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAssignRfidSubmit();
                }
              }}
              placeholder={t('permitDetails.rfidTagPlaceholder')}
              disabled={rfidBusy}
              sx={rfidGunListening ? {
                '& .MuiOutlinedInput-root fieldset': { borderColor: '#00695c', borderWidth: 2 },
              } : undefined}
            />
            <Button
              variant={rfidGunListening ? 'contained' : 'outlined'}
              startIcon={<ScanGunIcon />}
              onClick={handleStartGunScan}
              disabled={rfidBusy}
              sx={rfidGunListening ? {
                whiteSpace: 'nowrap',
                bgcolor: '#00695c',
                '&:hover': { bgcolor: '#004d40' },
                '& .MuiButton-startIcon svg': { animation: 'rfid-pulse 1.4s ease-in-out infinite' },
                '@keyframes rfid-pulse': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.6, transform: 'scale(1.15)' },
                },
              } : {
                whiteSpace: 'nowrap',
                borderColor: '#00695c',
                color: '#00695c',
                '&:hover': { bgcolor: '#e0f2f1', borderColor: '#004d40' },
                '& .MuiButton-startIcon': { transition: 'transform 0.2s ease-out' },
                '&:hover .MuiButton-startIcon': { transform: 'scale(1.15)' },
              }}
            >
              {rfidGunListening ? t('permitDetails.scanWithGunListeningBtn') : t('permitDetails.scanWithGunBtn')}
            </Button>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', color: '#00695c', mb: 3, minHeight: '1.2em' }}>
            {rfidGunListening ? t('permitDetails.scanWithGunListening') : ' '}
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('permitDetails.rfidHistoryTitle')}
          </Typography>
          {rfidHistory.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>{t('permitDetails.rfidTagCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.actionCol')}</strong></TableCell>
                    <TableCell><strong>{t('permitDetails.performedByField')}</strong></TableCell>
                    <TableCell><strong>{t('permitDetails.dateTimeField')}</strong></TableCell>
                    {canDeleteRfid() && (
                      <TableCell align="right"><strong>{t('common.actions')}</strong></TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfidHistory.map((item, index) => {
                    const isActiveRow = !!rfidActiveTagUid && item.tag_uid === rfidActiveTagUid;
                    return (
                      <TableRow key={item.id || index} hover>
                        <TableCell>
                          {item.tag_uid}
                          {isActiveRow ? (
                            <Chip label={t('common.active')} color="success" size="small" sx={{ ml: 1 }} />
                          ) : (
                            <Chip label={t('permitDetails.rfidInactiveLabel')} color="default" size="small" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {t(`permitDetails.rfidAction${item.action.charAt(0).toUpperCase()}${item.action.slice(1)}`, { defaultValue: item.action })}
                        </TableCell>
                        <TableCell>{item.performed_by}</TableCell>
                        <TableCell>{new Date(item.timestamp).toLocaleString()}</TableCell>
                        {canDeleteRfid() && (
                          <TableCell align="right">
                            {isActiveRow && (
                              <Tooltip title={t('permitDetails.rfidDeactivateTooltip')}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={rfidBusy}
                                    onClick={handleDeactivateRfid}
                                    sx={{ color: '#d32f2f' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="textSecondary">{t('permitDetails.rfidNoHistory')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRfidDialogOpen(false)} disabled={rfidBusy}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleAssignRfidSubmit}
            disabled={rfidBusy || !rfidTagInput.trim()}
            startIcon={rfidLoading ? <CircularProgress size={16} /> : <NfcIcon />}
            sx={{ bgcolor: '#00695c', '&:hover': { bgcolor: '#004d40' } }}
          >
            {rfidLoading
              ? t('permitDetails.rfidAssigning')
              : rfidActiveTagUid ? t('permitDetails.rfidReplaceBtn') : t('permitDetails.rfidAssignBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Active / Inactive Toggle Dialog */}
      <Dialog open={inactiveDialogOpen} onClose={() => !inactiveLoading && setInactiveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: permit?.status === 'active' ? '#e65100' : '#2e7d32' }}>
          {permit?.status === 'active' ? t('permitDetails.suspendPermitTitle') : t('permitDetails.reactivatePermitTitle')}
        </DialogTitle>
        <DialogContent>
          <Alert severity={permit?.status === 'active' ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {permit?.status === 'active'
              ? t('permitDetails.suspendNotice')
              : t('permitDetails.reactivateNotice')}
          </Alert>
          {inactiveError && (
            <Alert severity="error" sx={{ mb: 2 }}>{inactiveError}</Alert>
          )}
          <TextField
            label={t('permitDetails.reasonOptionalLabel')}
            fullWidth
            multiline
            rows={3}
            value={inactiveReason}
            onChange={e => setInactiveReason(e.target.value)}
            placeholder={permit?.status === 'active' ? t('permitDetails.suspendReasonPlaceholder') : t('permitDetails.reactivateReasonPlaceholder')}
            disabled={inactiveLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInactiveDialogOpen(false)} disabled={inactiveLoading}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleInactiveSubmit}
            disabled={inactiveLoading}
            startIcon={inactiveLoading ? <CircularProgress size={16} /> : null}
            sx={permit?.status === 'active'
              ? { bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }
              : { bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }
            }
          >
            {inactiveLoading ? t('profile.saving') : permit?.status === 'active' ? t('permitDetails.confirmSuspendBtn') : t('permitDetails.confirmActivateBtn')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermitDetails;
